import React from "react";
import { createPortal } from "react-dom";
import { Grid3x3, Sparkles, Check } from "lucide-react";
import { formatPrice } from "../../lib/matrix";

// Off-screen, fixed-size branded card that gets rasterised for PNG / PDF sharing.
export default function ShareCard({ cardRef, title, subtitle, tags = [], items = [], features = [], pricing, priceMode, note }) {
  const symbol = pricing?.symbol || "$";
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const url = `${window.location.origin}${window.location.pathname}`;
  return createPortal(
    <div className="share-stage" aria-hidden="true">
      <div className="share-card" ref={cardRef} data-testid="share-card">
        <div className="share-top">
          <div className="share-brand">
            <span className="share-mark">
              <Grid3x3 size={20} strokeWidth={2.4} />
            </span>
            <span>
              Microsoft <b>License Hub</b>
            </span>
          </div>
          <div className="share-meta">
            <span className="share-chip">{pricing?.currency || "USD"} · {pricing?.label || "United States"}</span>
            <span className="share-chip">{priceMode === "teams" ? "With Teams" : "No Teams"}</span>
            <span className="share-chip muted">{date}</span>
          </div>
        </div>

        <h1 className="share-title">{title}</h1>
        {subtitle && <p className="share-sub">{subtitle}</p>}
        {tags.length > 0 && (
          <div className="share-tags">
            {tags.map((t) => (
              <span key={t} className="share-chip">
                {t}
              </span>
            ))}
          </div>
        )}

        <ol className="share-list">
          {items.map((it, idx) => (
            <li key={it.key} className={`share-item ${idx === 0 && it.total !== null ? "top" : ""}`}>
              <span className="share-rank">{idx + 1}</span>
              <div className="share-main">
                <div className="share-parts">
                  {it.parts.map((p, i) => (
                    <React.Fragment key={p.id}>
                      {i > 0 && <span className="share-plus">+</span>}
                      <span className={`share-part ${p.kind !== "plan" ? "extra" : ""}`}>{p.kind === "plan" ? p.name : p.label}</span>
                    </React.Fragment>
                  ))}
                </div>
                <div className="share-line">
                  {it.badge && <span className="share-badge">{it.badge}</span>}
                  {idx === 0 && it.total !== null && (
                    <span className="share-badge best">
                      <Sparkles size={11} /> cheapest
                    </span>
                  )}
                </div>
                {it.coverage && (
                  <div className="share-line covs">
                    {it.coverage.map((c) => (
                      <span key={c.name} className="share-cov">
                        <Check size={11} /> {c.name} <em>via {c.via}</em>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="share-price">
                <div className="share-total">{it.total === null || it.total === undefined ? "\u2014" : formatPrice(it.total, symbol, it.estimated)}</div>
                <div className="share-per">per user / month</div>
                {it.parts.length > 1 && (
                  <div className="share-break">{it.parts.map((x) => (x.price === null || x.price === undefined ? "?" : formatPrice(x.price, symbol, x.estimated))).join(" + ")}</div>
                )}
              </div>
            </li>
          ))}
        </ol>

        {features.length > 0 && (
          <div className="share-features">
            {features.map((f) => (
              <span key={f} className="share-chip">
                {f}
              </span>
            ))}
          </div>
        )}

        <div className="share-foot">
          <span>
            {pricing?.note}. {note} {"\u2248"} = estimated from US list price. Verify with Microsoft before purchasing.
          </span>
          <span className="share-url">{url}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
